import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import "./ScheduleEditor.css";

function ScheduleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    description: "",
    startTime: "",
    endTime: "",
    priority: "normal",
    calendar: "private",
    tags: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  // Load existing schedule when editing
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient
      .get(`/schedules/${id}`)
      .then((data) => {
        const s = data.schedule;
        setForm({
          title: s.title || "",
          description: s.description || "",
          startTime: (s.startTime || "").slice(0, 16), // datetime-local format
          endTime: (s.endTime || "").slice(0, 16),
          priority: s.priority || "normal",
          calendar: s.calendar || "private",
          tags: Array.isArray(s.tags) ? s.tags.join(", ") : s.tags || "",
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const payload = {
      title: form.title,
      description: form.description,
      startTime: form.startTime.length === 16 ? form.startTime + ":00" : form.startTime,
      endTime: form.endTime.length === 16 ? form.endTime + ":00" : form.endTime,
      priority: form.priority,
      calendar: form.calendar,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };

    try {
      if (isEditing) {
        await apiClient.put(`/schedules/${id}`, payload);
      } else {
        await apiClient.post("/schedules", payload);
      }
      navigate("/calendar");
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm("Delete this event? This will also remove associated reminders.")))
      return;
    setSaving(true);
    try {
      await apiClient.delete(`/schedules/${id}`);
      navigate("/calendar");
    } catch (err) {
      setError(err.message || "Failed to delete");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="schedule-editor">
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div className="schedule-editor">
      {confirmDialog}
      <h2>{isEditing ? "Edit Event" : "New Event"}</h2>

      {error && <div className="editor-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Event title"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Optional description"
            rows={3}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startTime">Start</label>
            <input
              id="startTime"
              name="startTime"
              type="datetime-local"
              value={form.startTime}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="endTime">End</label>
            <input
              id="endTime"
              name="endTime"
              type="datetime-local"
              value={form.endTime}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={form.priority}
              onChange={handleChange}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="calendar">Calendar</label>
            <select
              id="calendar"
              name="calendar"
              value={form.calendar}
              onChange={handleChange}
            >
              <option value="private">Private</option>
              <option value="work">Work</option>
              <option value="holidays">Holidays</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              id="tags"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              placeholder="Comma-separated tags"
            />
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update" : "Create"}
          </button>
          {isEditing && (
            <button
              type="button"
              className="btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/calendar")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ScheduleEditor;
